import { useToast } from "@dash/toast";
import { useDeviceType } from "@dash/mq";
import { HeaderBar, Page } from "@dash/page";
import { useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";
import { Serializers, type Email } from "@megaron/serializers";
import { Uuid } from "@megaron/uuid";
import type { TicketCategory } from "@megaron/support-contracts";

import { AttachmentEntry } from "@megarax/crm-customers";

import { SupportCreateTicketFormBody } from "../create/SupportCreateTicketFormBody";
import type { TicketProductDraft } from "../products/SupportTicketProductsField";
import {
  buildSupportCreateTicketContactOptions,
  buildTagsFromSelectedReasons,
} from "../create/supportTicketReasonCategories";

type Props = {
  listPath?: string;
};

type FormParams = {
  listPath: string;
  customerUuid?: Uuid;
  defaultReporterEmail?: string;
  defaultReporterName?: string;
};

function useSupportCreateTicketForm(params: FormParams) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { iamUser } = useContext(IamAuthContext);
  const support = useClientManager("support");
  const crm = useClientManager("crm");

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reporterName, setReporterName] = useState(params.defaultReporterName ?? "");
  const [reporterEmail, setReporterEmail] = useState(params.defaultReporterEmail ?? "");
  const [selectedContact, setSelectedContact] = useState("");
  const [sourceMailbox, setSourceMailbox] = useState("");
  const [attachments, setAttachments] = useState<AttachmentEntry[]>([]);
  const [selectedReasonIds, setSelectedReasonIds] = useState<string[]>([]);
  const [category, setCategory] = useState<TicketCategory | null>(null);
  const [products, setProducts] = useState<TicketProductDraft[]>([]);
  const [invoiceNumber, setInvoiceNumber] = useState("");

  const mailboxStatusQuery = support.imapConnectionStatus().useQuery({});
  const customerQuery = crm.customerDocBulkQuery().useQuery(
    { customerUuids: params.customerUuid ? [params.customerUuid] : [] },
    { enabled: !!params.customerUuid },
  );
  const contactsQuery = crm.customerContactsQuery().useQuery(
    { customerId: params.customerUuid ?? Uuid("00000000-0000-0000-0000-000000000000") },
    { enabled: !!params.customerUuid },
  );

  const mailboxOptions = useMemo(
    () => (mailboxStatusQuery.data?.mailboxes ?? []).map((mailbox) => ({ label: mailbox.email, value: mailbox.email })),
    [mailboxStatusQuery.data?.mailboxes],
  );

  const contactOptions = useMemo(
    () =>
      buildSupportCreateTicketContactOptions(!!params.customerUuid, customerQuery.data?.[0], contactsQuery.data ?? []),
    [contactsQuery.data, customerQuery.data, params.customerUuid],
  );

  useEffect(() => {
    if (!params.customerUuid || contactOptions.length === 0) return;
    const current = contactOptions.find(
      (option) => option.email === reporterEmail.trim().toLowerCase() && option.name === reporterName.trim(),
    );
    if (current) {
      setSelectedContact(current.value);
      return;
    }
    const byEmail = contactOptions.find((option) => option.email === reporterEmail.trim().toLowerCase());
    if (byEmail) {
      setSelectedContact(byEmail.value);
      setReporterName(byEmail.name);
      return;
    }
    const first = contactOptions[0];
    setSelectedContact(first.value);
    setReporterEmail(first.email);
    setReporterName(first.name);
  }, [contactOptions, params.customerUuid, reporterEmail, reporterName]);

  const createTicketMutation = support.createTicket().useMutation({
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ["support", "ticketsList"] });
      toast.info("Utworzono zgłoszenie");
      navigate(`${params.listPath}/${ticket.id}`);
    },
    onError: () => {
      toast.error("Nie udało się utworzyć zgłoszenia");
    },
  });

  const isBusy = "isLoading" in createTicketMutation ? createTicketMutation.isLoading : false;
  const hasCustomerContext = !!params.customerUuid;

  const onContactChange = (value: string) => {
    const selected = contactOptions.find((option) => option.value === value);
    if (!selected) return;
    setSelectedContact(selected.value);
    setReporterEmail(selected.email);
    setReporterName(selected.name);
  };

  const onCreate = () => {
    const trimmedSubject = subject.trim();
    const trimmedReporterEmail = reporterEmail.trim();
    const trimmedReporterName = reporterName.trim();
    const trimmedSourceMailbox = sourceMailbox.trim();
    const trimmedMessage = message.trim();
    if (!trimmedSubject || !trimmedMessage) {
      toast.error("Uzupełnij tytuł i wiadomość");
      return;
    }
    let resolvedReporterEmail: Email | null = null;
    if (trimmedReporterEmail) {
      const reporterEmailResult = Serializers.email.deserialize(trimmedReporterEmail);
      if (reporterEmailResult.isFailure) {
        toast.error("Podaj poprawny email kontaktu");
        return;
      }
      resolvedReporterEmail = reporterEmailResult.value;
    }
    if (hasCustomerContext && !resolvedReporterEmail) {
      toast.error("Wybierz kontakt");
      return;
    }
    const tags = buildTagsFromSelectedReasons(selectedReasonIds);
    createTicketMutation.mutate({
      subject: trimmedSubject,
      message: trimmedMessage,
      reporterEmail: resolvedReporterEmail,
      reporterName:
        trimmedReporterName ||
        (trimmedReporterEmail ? trimmedReporterEmail.split("@")[0] : ""),
      sourceMailbox: trimmedSourceMailbox || null,
      status: "open",
      priority: null,
      category,
      tags: tags.length > 0 ? tags : null,
      assigneeEmails: iamUser?.email ? [iamUser.email] : null,
      attachments: attachments.length > 0 ? attachments : null,
      products: products.length > 0 ? products : null,
      invoiceNumber: invoiceNumber.trim() || null,
    });
  };

  return {
    subject,
    setSubject,
    message,
    setMessage,
    reporterName,
    setReporterName,
    reporterEmail,
    setReporterEmail,
    selectedContact,
    onContactChange,
    sourceMailbox,
    setSourceMailbox,
    mailboxOptions,
    isMailboxLoading: mailboxStatusQuery.isLoading,
    contactOptions,
    hasCustomerContext,
    attachments,
    setAttachments,
    selectedReasonIds,
    setSelectedReasonIds,
    category,
    setCategory,
    products,
    setProducts,
    invoiceNumber,
    setInvoiceNumber,
    onCreate,
    isBusy,
  };
}

export const SupportCreateTicketPage = ({ listPath = "/support/tickets" }: Props) => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const [searchParams] = useSearchParams();
  const customerUuidParam = searchParams.get("customerUuid");
  const customerUuid = customerUuidParam ? Uuid(customerUuidParam) : undefined;

  const form = useSupportCreateTicketForm({
    listPath,
    customerUuid,
    defaultReporterEmail: searchParams.get("reporterEmail") ?? undefined,
    defaultReporterName: searchParams.get("reporterName") ?? undefined,
  });

  useEffect(() => {
    document.title = "Nowe zgłoszenie";
  }, []);

  return (
    <>
      <HeaderBar header="Nowe zgłoszenie" onGoBack={() => navigate(listPath)} />
      <Page
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          marginTop: isMobile ? "4.5rem" : "5.5rem",
          padding: isMobile ? "0 1rem" : "0 2rem",
          marginBottom: "2rem",
        }}
      >
        <SupportCreateTicketFormBody isMobile={isMobile} {...form} />
      </Page>
    </>
  );
};
